import React, { useEffect, useRef, useState } from 'react';
import { Card, CardContent } from './ui/card';
import { TrendingUp, Users, Target, Repeat, ArrowUpRight } from 'lucide-react';

const UseCases = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  const useCases = [
    {
      icon: TrendingUp,
      title: 'Lead Generation & Qualification',
      description: 'AI agents autonomously identify, engage, and qualify prospects across multiple channels, delivering sales-ready leads to your team.',
      impact: '3x increase in qualified leads',
      image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzZ8MHwxfHNlYXJjaHwzfHxidXNpbmVzcyUyMGdyb3d0aHxlbnwwfHx8fDE3NzEzNzI5Mzh8MA&ixlib=rb-4.1.0&q=85'
    },
    {
      icon: Users,
      title: 'Customer Engagement & Nurturing',
      description: 'Maintain consistent communication with prospects and customers, providing timely responses and personalized content at scale.',
      impact: '5x faster response times',
      image: 'https://images.unsplash.com/photo-1576267423048-15c0040fec78?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NjZ8MHwxfHNlYXJjaHw0fHxidXNpbmVzcyUyMHN1Y2Nlc3N8ZW58MHx8fHwxNzcxMzcyOTQzfDA&ixlib=rb-4.1.0&q=85'
    },
    {
      icon: Target,
      title: 'Outbound Sales Campaigns',
      description: 'Execute sophisticated multi-touch campaigns with AI-powered personalization that adapts based on prospect behavior and engagement.',
      impact: '40% higher conversion rates',
      image: 'https://images.pexels.com/photos/590011/pexels-photo-590011.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940'
    },
    {
      icon: Repeat,
      title: 'Account Management & Expansion',
      description: 'Monitor customer health, identify upsell opportunities, and automate routine touchpoints to maximize account lifetime value.',
      impact: '25% increase in retention',
      image: 'https://images.unsplash.com/photo-1563132337-f159f484226c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjh8MHwxfHNlYXJjaHw0fHxidXNpbmVzcyUyMGV4ZWN1dGl2ZXN8ZW58MHx8fHwxNzcxMzcyOTM1fDA&ixlib=rb-4.1.0&q=85'
    }
  ];

  return (
    <section id="usecases" ref={sectionRef} className="relative py-24 md:py-32 bg-gradient-to-b from-black via-[#0a0a0a] to-black overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-1/3 right-0 w-96 h-96 bg-[#FF6600] rounded-full mix-blend-multiply filter blur-3xl opacity-10"></div>
      </div>

      <div className="relative z-10 container mx-auto px-6">
        {/* Section Header */}
        <div className="max-w-3xl mx-auto text-center mb-16">
          <div
            className={`inline-flex items-center space-x-2 bg-[#FF6600]/10 border border-[#FF6600]/30 rounded-full px-5 py-2 mb-6 transition-all duration-700 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
            }`}
          >
            <Target className="w-4 h-4 text-[#FF6600]" />
            <span className="text-[#FF6600] font-semibold text-sm">Proven Results</span>
          </div>
          <h2
            className={`text-4xl md:text-5xl lg:text-6xl font-bold mb-6 transition-all duration-700 delay-100 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
            }`}
          >
            <span className="text-gradient-orange">70% Growth</span>, 30% Maintenance
          </h2>
          <p
            className={`text-lg md:text-xl text-gray-400 transition-all duration-700 delay-200 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
            }`}
          >
            Transform your sales organization from reactive to proactive. Let AI handle routine maintenance so your team can focus on strategic growth initiatives.
          </p>
        </div>

        {/* Growth Philosophy */}
        <div
          className={`max-w-5xl mx-auto mb-20 transition-all duration-700 delay-300 ${
            isVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-95'
          }`}
        >
          <Card className="glass-morphism border-[#FF6600]/20">
            <CardContent className="p-8 md:p-12">
              <div className="grid md:grid-cols-2 gap-8">
                <div className="border-r border-white/10 pr-8">
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="text-6xl font-bold text-[#FF6600]">70%</div>
                    <TrendingUp className="w-12 h-12 text-[#FF6600]" />
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-3">Growth Activities</h3>
                  <p className="text-gray-400 leading-relaxed">
                    AI agents handle prospecting, lead generation, initial outreach, and qualification - freeing your team to focus on closing deals and building relationships.
                  </p>
                </div>
                <div className="pl-0 md:pl-8">
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="text-6xl font-bold text-white/70">30%</div>
                    <Repeat className="w-12 h-12 text-white/70" />
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-3">Maintenance Tasks</h3>
                  <p className="text-gray-400 leading-relaxed">
                    Automated systems manage customer support, renewals, routine check-ins, and account administration - ensuring nothing falls through the cracks.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Use Cases Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {useCases.map((useCase, index) => {
            const Icon = useCase.icon;
            return (
              <Card
                key={index}
                className={`glass-morphism border-white/10 overflow-hidden hover-lift transition-all duration-700 group cursor-pointer ${
                  isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                }`}
                style={{ transitionDelay: `${500 + index * 100}ms` }}
              >
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={useCase.image}
                    alt={useCase.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent"></div>
                  <div className="absolute top-4 right-4 w-12 h-12 rounded-lg bg-[#FF6600]/90 backdrop-blur-sm flex items-center justify-center">
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                </div>
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold text-white mb-3 group-hover:text-[#FF6600] transition-colors duration-300">
                    {useCase.title}
                  </h3>
                  <p className="text-gray-400 mb-4 leading-relaxed">{useCase.description}</p>
                  <div className="flex items-center justify-between pt-4 border-t border-white/10">
                    <span className="text-[#FF6600] font-semibold flex items-center">
                      {useCase.impact}
                      <ArrowUpRight className="w-4 h-4 ml-1" />
                    </span>
                    <button className="text-gray-400 hover:text-[#FF6600] transition-colors duration-200 text-sm font-medium">
                      Learn More →
                    </button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default UseCases;
